// Inline SVG diagram — "Architecture pour 50 sites" — used on
// MediaGroupsPage. Built as inline JSX so currentColor and CSS variables
// theme it via the surrounding section (dark mode, ink tone, etc.).
//
// Layout: a central "Console groupe" node connects to four cluster nodes
// (Région A/B/C/D), each fanning out to N site dots. Numbers are
// illustrative — the spec asks for the *shape* of 50 sites, not exact.

import type { CSSProperties } from "react";

export interface ArchitectureDiagramProps {
  /** Localised labels — passed by the parent page so i18n stays at page level. */
  labels: {
    title: string;
    groupConsole: string;
    region: string;
    sites: string;
    audit: string;
    rgpd: string;
  };
  className?: string;
  style?: CSSProperties;
}

export function ArchitectureDiagram({ labels, className, style }: ArchitectureDiagramProps) {
  return (
    <svg
      viewBox="0 0 720 360"
      className={className}
      style={style}
      role="img"
      aria-label={labels.title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{labels.title}</title>

      {/* Central console node */}
      <g transform="translate(280, 140)" fill="currentColor">
        <rect x="0" y="0" width="160" height="60" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <text x="80" y="28" textAnchor="middle" fontFamily="Georgia,serif" fontSize="14" fontWeight="600">
          {labels.groupConsole}
        </text>
        <text x="80" y="46" textAnchor="middle" fontFamily="ui-sans-serif,system-ui" fontSize="10" opacity="0.7">
          {labels.audit} · {labels.rgpd}
        </text>
      </g>

      {/* Four region clusters */}
      {([
        { x: 80,  y: 60,  region: "A" },
        { x: 80,  y: 260, region: "B" },
        { x: 560, y: 60,  region: "C" },
        { x: 560, y: 260, region: "D" },
      ] as const).map((cluster, idx) => (
        <g key={idx}>
          {/* Connector line from center to cluster */}
          <line
            x1={360}
            y1={170}
            x2={cluster.x + 60}
            y2={cluster.y + 20}
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.4"
          />
          {/* Region label box */}
          <g transform={`translate(${cluster.x}, ${cluster.y})`}>
            <rect width="120" height="40" fill="none" stroke="currentColor" strokeWidth="1" />
            <text x="60" y="18" textAnchor="middle" fontFamily="Georgia,serif" fontSize="12" fontWeight="500">
              {labels.region} {cluster.region}
            </text>
            <text x="60" y="32" textAnchor="middle" fontFamily="ui-sans-serif,system-ui" fontSize="9" opacity="0.65">
              12 {labels.sites}
            </text>
          </g>

          {/* Twelve site dots arranged in a 4×3 grid below the region */}
          {Array.from({ length: 12 }).map((_, i) => {
            const col = i % 4;
            const row = Math.floor(i / 4);
            const dotX = cluster.x + 10 + col * 30;
            const dotY = cluster.y + 50 + row * 16;
            return (
              <circle
                key={i}
                cx={dotX}
                cy={dotY}
                r={3.5}
                fill="currentColor"
                opacity={0.85}
              />
            );
          })}
        </g>
      ))}

      {/* Footer caption */}
      <text x="360" y="346" textAnchor="middle" fontFamily="ui-sans-serif,system-ui" fontSize="10" fill="currentColor" opacity="0.65">
        48 {labels.sites} · 4 {labels.region.toLowerCase()}s · 1 {labels.groupConsole.toLowerCase()}
      </text>
    </svg>
  );
}
